import React, {useEffect, useState} from 'react'
import {useCustomerStore} from '../../store/hooks/useStore'
import { isMobile} from '../../utils/utils'
import {Box, Button, Center, Flex, Stack, Text, BaseButton} from '../../vanilla'
import {FeefoVerifiedIcon, IcelandVerifiedIcon} from '../icons'
import {Link} from '../link'
import {ReviewStars} from '../review-stars'
import {useCredentialModal} from '../../contexts'
import {CredentialModalPlacementMap} from '../../types/ui'
import useCustomSitePreference from '../../hooks/forms/useCustomSitePreference'
import ReviewDisplay from './review-display'
import {useProductReviews} from '../../hooks/use-product-reviews'

interface FeefoReviewsProps {
  productId?: string
  writeReviewUrl: string | null
  hasReviews: boolean
}

export interface IReview {
  products: {
    rating: {
      rating: number
    }
    review: string
  }[]
  customer: {
    display_name: string
  } | null
  last_updated_date: string
}

const FEEFO_API_PAGE_SIZE = 6

const FeefoReviews = (props: FeefoReviewsProps) => {
  const {productId, writeReviewUrl, hasReviews} = props
  const [reviews, setReviews] = useState<IReview[]>([])
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const {isRegistered} = useCustomerStore()
  const credentialModal = useCredentialModal()
  const [feefoMerchantIdentifier, setFeefoMerchantIdentifier] = useState<string>('')
  const [reviewSummary, setReviewSummary] = useState({
    pages: 0,
    count: 0,
  })
  const feefoSupplierMerchantIdentifierPreference = useCustomSitePreference(
    'FeefoSupplierMerchantIdentifier',
    '',
  )
  const showReviews = useCustomSitePreference('ShowReviews')
  const showLeaveAReview = useCustomSitePreference('showLeaveAReview')
  const isMobileDevice = isMobile()

  const {currentCustomerGroups} = useCustomerStore()
  const {isGroupRestricted} = useProductReviews()
  const [isCustomerInARestrictedGroup, setIsCustomerInARestrictedGroup] = useState<boolean>(true)

  useEffect(() => {
    setIsCustomerInARestrictedGroup(currentCustomerGroups.some(isGroupRestricted))
  }, [currentCustomerGroups])

  useEffect(() => {
    setFeefoMerchantIdentifier(feefoSupplierMerchantIdentifierPreference)
  }, [feefoSupplierMerchantIdentifierPreference])

  useEffect(() => {
    const fetchReviews = async () => {
      await loadReviews()
    }
    if (hasReviews) {
      fetchReviews().catch(console.error)
    }
  }, [productId, isRegistered, hasReviews, feefoMerchantIdentifier])

  const loadReviews = async () => {
    setIsLoading(true)
    if (feefoMerchantIdentifier) {
      const result = await fetch(
        `https://api.feefo.com/api/20/importedreviews/product?merchant_identifier=${feefoMerchantIdentifier}&product_sku=${productId}&full_thread=include&since_period=all&unanswered_feedback=exclude&page_size=${FEEFO_API_PAGE_SIZE}&page=${page}`,
      ).then((response) => response.json())
      if (result) {
        setReviews((prevReviews) => [...prevReviews, ...result.reviews])
        setReviewSummary({
          pages: result.summary.meta.pages,
          count: result.summary.meta.count,
        })
        setPage((prevPage) => prevPage + 1)
      }
      setIsLoading(false)
    }
  }
  return (
    <Box width="full" bg="white">
      <Stack>
        <Stack
          direction={['column', 'row', 'row']}
          padding="20px"
          gap="16px"
          justifyContent="space-between"
        >
          <Flex>
            <Stack>
              <Text variant="heading0">Product Reviews</Text>
              {showReviews && (
                <Text
                  variant="text3"
                  color="textMuted"
                >{`(${reviewSummary?.count} product reviews)`}</Text>
              )}
            </Stack>
          </Flex>
          {showLeaveAReview && (
            <Flex>
            {isRegistered ? (
              !isCustomerInARestrictedGroup &&
              <BaseButton
                variant="secondary"
                width="auto"
                size="sm"
                {...(writeReviewUrl ? {as: Link, to: writeReviewUrl} : {})}
              >
                <Text variant="text4">Write a review</Text>
              </BaseButton>
            ) : (
                <Button
                  variant="outlineDark"
                  width="auto"
                  size="sm"
                  onClick={() => {
                    const modalPlacement = isMobileDevice
                      ? CredentialModalPlacementMap.BOTTOM
                      : CredentialModalPlacementMap.ATTACHED
                    credentialModal.setModalPlacementType(modalPlacement)
                    credentialModal.onToggle()
                  }}
                >
                  Sign in to leave a review
                </Button>
              )}
            </Flex>
          )}
        </Stack>
        {showReviews && (
          <ReviewDisplay
            reviews={reviews}
            isLoading={isLoading}
            reviewSummary={reviewSummary}
            loadReviews={loadReviews}
            page={page}
          />
        )}
      </Stack>
    </Box>
  )
}

export default FeefoReviews
